import React, { useState, useEffect, useRef, useCallback } from "react";
import Navbar from "./Navbar";

const BASE_URL =
  window.location.hostname === "localhost"
    ? "http://localhost:5000"
    : "https://success-blog-app-backend.onrender.com";

// ── FILTERS ──
const FILTERS = [
  { name: "Original",  css: "none",                                                               emoji: "✦" },
  { name: "Clarendon", css: "contrast(1.2) saturate(1.35) brightness(1.05)",                      emoji: "🌟" },
  { name: "Gingham",   css: "brightness(1.06) hue-rotate(-10deg) contrast(0.9)",                  emoji: "🌿" },
  { name: "Moon",      css: "grayscale(1) contrast(1.1) brightness(1.08)",                         emoji: "🌕" },
  { name: "Lark",      css: "brightness(1.12) contrast(0.85) saturate(1.2)",                      emoji: "🐦" },
  { name: "Reyes",     css: "sepia(0.22) brightness(1.1) contrast(0.85) saturate(0.75)",           emoji: "🌅" },
  { name: "Juno",      css: "saturate(1.4) contrast(1.1) brightness(1.05) hue-rotate(-5deg)",     emoji: "💛" },
  { name: "Slumber",   css: "saturate(0.7) brightness(1.05) sepia(0.15)",                         emoji: "😴" },
  { name: "Crema",     css: "saturate(0.85) contrast(0.95) brightness(1.12) sepia(0.1)",          emoji: "🍦" },
  { name: "Ludwig",    css: "saturate(0.9) contrast(1.05) brightness(1.08) sepia(0.06)",          emoji: "🎨" },
  { name: "Aden",      css: "hue-rotate(-20deg) saturate(0.85) brightness(1.2) contrast(0.85)",   emoji: "🌺" },
  { name: "Perpetua",  css: "saturate(1.1) contrast(1.1) brightness(1.02)",                       emoji: "🌊" },
  { name: "Neon",      css: "saturate(2.4) hue-rotate(30deg) contrast(1.25) brightness(0.95)",    emoji: "⚡" },
  { name: "Drama",     css: "contrast(1.5) brightness(0.86) saturate(1.35)",                      emoji: "🎬" },
  { name: "Fade",      css: "brightness(1.14) contrast(0.76) saturate(0.7)",                      emoji: "🌫" },
  { name: "Vintage",   css: "sepia(0.55) contrast(1.15) brightness(0.88)",                        emoji: "📷" },
];

// ── CROP RATIOS ──
const RATIOS = [
  { label: "Free",  value: 0,       icon: "⛶" },
  { label: "1:1",   value: 1,       icon: "□" },
  { label: "4:5",   value: 4/5,     icon: "▯" },
  { label: "9:16",  value: 9/16,    icon: "▮" },
  { label: "16:9",  value: 16/9,    icon: "▬" },
];

// ─────────────────────────────────────────────────────────
// CROP EDITOR
// ─────────────────────────────────────────────────────────
function CropEditor({ src, onDone, onCancel }) {
  const canvasRef    = useRef(null);
  const imgRef       = useRef(null);
  const containerRef = useRef(null);
  const cropRef      = useRef({ x:0, y:0, w:0, h:0 });
  const dragRef      = useRef(null);
  const [ratio, setRatio]         = useState(1);
  const [imgLoaded, setImgLoaded] = useState(false);
  const HANDLE = 14;

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const img    = imgRef.current;
    if (!canvas || !img || !imgLoaded) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const { x, y, w, h } = cropRef.current;
    ctx.fillStyle = "rgba(0,0,0,0.55)";
    ctx.fillRect(0, 0, canvas.width, y);
    ctx.fillRect(0, y+h, canvas.width, canvas.height-y-h);
    ctx.fillRect(0, y, x, h);
    ctx.fillRect(x+w, y, canvas.width-x-w, h);
    ctx.strokeStyle = "#ffa028"; ctx.lineWidth = 2;
    ctx.strokeRect(x, y, w, h);
    ctx.strokeStyle = "rgba(255,160,40,0.3)"; ctx.lineWidth = 0.7;
    for (let i=1;i<=2;i++){
      ctx.beginPath();ctx.moveTo(x+w*i/3,y);ctx.lineTo(x+w*i/3,y+h);ctx.stroke();
      ctx.beginPath();ctx.moveTo(x,y+h*i/3);ctx.lineTo(x+w,y+h*i/3);ctx.stroke();
    }
    getHandles().forEach(({hx,hy})=>{
      ctx.fillStyle="#ffa028";ctx.beginPath();ctx.arc(hx,hy,HANDLE/2,0,Math.PI*2);ctx.fill();
    });
    const L=20; ctx.strokeStyle="#fff"; ctx.lineWidth=2.5;
    [[x,y,L,0,0,L],[x+w,y,-L,0,0,L],[x,y+h,L,0,0,-L],[x+w,y+h,-L,0,0,-L]].forEach(([cx,cy,d1x,d1y,d2x,d2y])=>{
      ctx.beginPath();ctx.moveTo(cx,cy);ctx.lineTo(cx+d1x,cy+d1y);ctx.stroke();
      ctx.beginPath();ctx.moveTo(cx,cy);ctx.lineTo(cx+d2x,cy+d2y);ctx.stroke();
    });
  }, [imgLoaded]);

  const getHandles = () => {
    const {x,y,w,h}=cropRef.current;
    return [
      {id:"tl",hx:x,    hy:y},{id:"tr",hx:x+w,  hy:y},
      {id:"bl",hx:x,    hy:y+h},{id:"br",hx:x+w,hy:y+h},
      {id:"tm",hx:x+w/2,hy:y},{id:"bm",hx:x+w/2,hy:y+h},
      {id:"ml",hx:x,    hy:y+h/2},{id:"mr",hx:x+w,hy:y+h/2},
    ];
  };

  const initCrop = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas||!imgLoaded) return;
    const CW=canvas.width, CH=canvas.height;
    if (ratio===0) {
      cropRef.current={x:CW*0.05,y:CH*0.05,w:CW*0.9,h:CH*0.9};
    } else {
      let cw,ch;
      if(CW/CH>ratio){ch=CH*0.9;cw=ch*ratio;}else{cw=CW*0.9;ch=cw/ratio;}
      cropRef.current={x:(CW-cw)/2,y:(CH-ch)/2,w:cw,h:ch};
    }
    draw();
  },[ratio,imgLoaded,draw]);

  useEffect(()=>{initCrop();},[initCrop]);

  useEffect(()=>{
    const img=new Image();
    img.onload=()=>{
      imgRef.current=img;
      const con=containerRef.current, canvas=canvasRef.current;
      if(!con||!canvas) return;
      const mW=con.clientWidth||500, mH=window.innerHeight*0.52;
      const sc=Math.min(mW/img.naturalWidth,mH/img.naturalHeight,1);
      canvas.width=Math.round(img.naturalWidth*sc);
      canvas.height=Math.round(img.naturalHeight*sc);
      setImgLoaded(true);
    };
    img.src=src;
  },[src]);

  useEffect(()=>{if(imgLoaded)draw();},[imgLoaded,draw]);

  const getPos=(e,canvas)=>{
    const r=canvas.getBoundingClientRect();
    const sx=canvas.width/r.width, sy=canvas.height/r.height;
    const cx=e.touches?e.touches[0].clientX:e.clientX;
    const cy=e.touches?e.touches[0].clientY:e.clientY;
    return{x:(cx-r.left)*sx,y:(cy-r.top)*sy};
  };
  const hitHandle=(px,py)=>getHandles().find(({hx,hy})=>Math.hypot(px-hx,py-hy)<HANDLE+4);
  const clamp=(v,a,b)=>Math.max(a,Math.min(b,v));
  const MIN=40;

  const onDown=(e)=>{
    e.preventDefault();
    const canvas=canvasRef.current;
    const{x:px,y:py}=getPos(e,canvas);
    const hit=hitHandle(px,py);
    if(hit){dragRef.current={type:"resize",handle:hit.id,startX:px,startY:py,startCrop:{...cropRef.current}};}
    else{
      const{x,y,w,h}=cropRef.current;
      if(px>=x&&px<=x+w&&py>=y&&py<=y+h)
        dragRef.current={type:"move",startX:px,startY:py,startCrop:{...cropRef.current}};
    }
  };
  const onMove=(e)=>{
    e.preventDefault();
    if(!dragRef.current) return;
    const canvas=canvasRef.current;
    const CW=canvas.width, CH=canvas.height;
    const{x:px,y:py}=getPos(e,canvas);
    const dx=px-dragRef.current.startX, dy=py-dragRef.current.startY;
    let{x,y,w,h}=dragRef.current.startCrop;
    if(dragRef.current.type==="move"){
      cropRef.current={x:clamp(x+dx,0,CW-w),y:clamp(y+dy,0,CH-h),w,h};
    } else {
      const id=dragRef.current.handle;
      let nx=x,ny=y,nw=w,nh=h;
      if(id==="tl"){nx=clamp(x+dx,0,x+w-MIN);ny=clamp(y+dy,0,y+h-MIN);nw=x+w-nx;nh=y+h-ny;}
      if(id==="tr"){nw=clamp(w+dx,MIN,CW-x);ny=clamp(y+dy,0,y+h-MIN);nh=y+h-ny;}
      if(id==="bl"){nx=clamp(x+dx,0,x+w-MIN);nw=x+w-nx;nh=clamp(h+dy,MIN,CH-y);}
      if(id==="br"){nw=clamp(w+dx,MIN,CW-x);nh=clamp(h+dy,MIN,CH-y);}
      if(id==="tm"){ny=clamp(y+dy,0,y+h-MIN);nh=y+h-ny;}
      if(id==="bm"){nh=clamp(h+dy,MIN,CH-y);}
      if(id==="ml"){nx=clamp(x+dx,0,x+w-MIN);nw=x+w-nx;}
      if(id==="mr"){nw=clamp(w+dx,MIN,CW-x);}
      if(ratio!==0&&["br","tl","tr","bl"].includes(id)){
        if(nw/nh>ratio) nh=nw/ratio; else nw=nh*ratio;
        nw=Math.min(nw,CW-nx); nh=Math.min(nh,CH-ny);
      }
      cropRef.current={x:nx,y:ny,w:nw,h:nh};
    }
    draw();
  };
  const onUp=()=>{dragRef.current=null;};

  const applyCrop=()=>{
    const canvas=canvasRef.current, img=imgRef.current;
    if(!canvas||!img) return;
    const{x,y,w,h}=cropRef.current;
    const sx=img.naturalWidth/canvas.width, sy=img.naturalHeight/canvas.height;
    const out=document.createElement("canvas");
    out.width=Math.round(w*sx); out.height=Math.round(h*sy);
    out.getContext("2d").drawImage(img,x*sx,y*sy,w*sx,h*sy,0,0,out.width,out.height);
    out.toBlob((blob)=>{if(blob)onDone(blob,URL.createObjectURL(blob));},"image/jpeg",0.93);
  };

  return (
    <div style={S.cropOverlay}>
      <div style={S.cropModal}>
        <div style={S.cropTopBar}>
          <button style={S.cropBarBtn} onClick={onCancel}>✕ Cancel</button>
          <span style={S.cropTitle}>✂ Crop</span>
          <button style={{...S.cropBarBtn,color:"#ffa028",fontWeight:700}} onClick={applyCrop}>Apply ✓</button>
        </div>
        <div style={S.ratioPills}>
          {RATIOS.map(r=>(
            <button key={r.label} style={{...S.ratioPill,...(ratio===r.value?S.ratioPillActive:{})}} onClick={()=>setRatio(r.value)}>
              <span style={{fontSize:14}}>{r.icon}</span><span>{r.label}</span>
            </button>
          ))}
        </div>
        <div ref={containerRef} style={S.cropCanvasWrap}>
          <canvas
            ref={canvasRef}
            style={{maxWidth:"100%",touchAction:"none",display:"block",borderRadius:12,cursor:"crosshair"}}
            onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
            onTouchStart={onDown} onTouchMove={onMove} onTouchEnd={onUp}
          />
        </div>
        <p style={{textAlign:"center",fontFamily:"JetBrains Mono,monospace",fontSize:10,color:"rgba(240,236,228,0.2)",letterSpacing:1}}>
          Drag corners to resize · Drag inside to move
        </p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// MAIN
// ─────────────────────────────────────────────────────────
function UploadStatus() {
  const [file,           setFile]           = useState(null);
  const [originalBlob,   setOriginalBlob]   = useState(null);
  const [preview,        setPreview]        = useState(null);
  const [myStatus,       setMyStatus]       = useState([]);
  const [uploading,      setUploading]      = useState(false);
  const [progress,       setProgress]       = useState(0);
  const [selectedFilter, setSelectedFilter] = useState(0);
  const [showCrop,       setShowCrop]       = useState(false);
  const [cropSrc,        setCropSrc]        = useState(null);
  const [activeTab,      setActiveTab]      = useState("filter");
  const [adjust,         setAdjust]         = useState({brightness:100,contrast:100,saturation:100,warmth:0,fade:0,vignette:0});
  const [viewer,         setViewer]         = useState(null);
  const [viewerFilter,   setViewerFilter]   = useState(0);
  const [viewsPopup,     setViewsPopup]     = useState(null);
  const [currentPage,    setCurrentPage]    = useState(1);
  const statusPerPage = 6;
  const fileRef = useRef(null);
  const userId  = localStorage.getItem("userId");

  const getMyStatus = async () => {
    try {
      const res  = await fetch(`${BASE_URL}/api/status/my/${userId}`);
      const data = await res.json();
      setMyStatus(Array.isArray(data)?data:[]);
    } catch(e){console.error(e);}
  };
  useEffect(()=>{if(userId)getMyStatus();},[]);

  // Free the previous preview's blob URL when it is replaced or the page
  // unmounts, so repeatedly picking/cropping media on mobile doesn't leak
  // decoded-image memory. Only the *previous* URL is revoked (via the cleanup
  // closure); the URL currently bound to <img>/<video src={preview}> is never
  // revoked while it is still rendered, so the preview never breaks.
  useEffect(()=>{
    if(!preview||!preview.startsWith("blob:")) return;
    return ()=>URL.revokeObjectURL(preview);
  },[preview]);

  const handleFileChange=(e)=>{
    const f=e.target.files[0]; if(!f) return;
    setFile(f); setSelectedFilter(0);
    setAdjust({brightness:100,contrast:100,saturation:100,warmth:0,fade:0,vignette:0});
    const url=URL.createObjectURL(f);
    setPreview(url); setOriginalBlob(null);
    if(f.type.startsWith("image")) setCropSrc(url);
  };

  const openCrop=()=>{
    if(!preview) return;
    setCropSrc(originalBlob?URL.createObjectURL(originalBlob):preview);
    setShowCrop(true);
  };
  const onCropDone=(blob,url)=>{
    setOriginalBlob(blob); setPreview(url); setShowCrop(false);
    setFile(new File([blob],file?.name||"cropped.jpg",{type:"image/jpeg"}));
  };

  const buildFilter=(fi,adj)=>{
    const base=FILTERS[fi].css==="none"?"":FILTERS[fi].css;
    const warm=adj.warmth>0?`hue-rotate(${adj.warmth*-0.3}deg) sepia(${adj.warmth*0.008})`:"";
    const fade=adj.fade>0?`brightness(${1+adj.fade*0.002}) contrast(${1-adj.fade*0.005}) saturate(${1-adj.fade*0.006})`:"";
    return [`brightness(${adj.brightness/100})`,`contrast(${adj.contrast/100})`,`saturate(${adj.saturation/100})`,warm,fade,base].filter(Boolean).join(" ");
  };

  const currentFilter = buildFilter(selectedFilter,adjust);
  const vigShadow     = adjust.vignette>0?`inset 0 0 ${adjust.vignette*2.5}px ${adjust.vignette*0.8}px rgba(0,0,0,0.7)`:"none";
  const isImage       = file&&!file.type.startsWith("video");

  const handleUpload=async()=>{
    if(!file){alert("Select a file first");return;}
    if(file.type.startsWith("video")){
      const vid=document.createElement("video");
      vid.preload="metadata"; vid.src=URL.createObjectURL(file);
      vid.onloadedmetadata=()=>{
        window.URL.revokeObjectURL(vid.src);
        if(vid.duration>30){alert("Video must be ≤ 30 seconds");return;}
        startUpload(file);
      };
      return;
    }
    const img=new Image(); img.src=preview;
    await new Promise(r=>(img.onload=r));
    const canvas=document.createElement("canvas");
    canvas.width=img.naturalWidth; canvas.height=img.naturalHeight;
    const ctx=canvas.getContext("2d");
    ctx.filter=currentFilter; ctx.drawImage(img,0,0);
    if(adjust.vignette>0){
      const g=ctx.createRadialGradient(canvas.width/2,canvas.height/2,canvas.width*0.3,canvas.width/2,canvas.height/2,canvas.width*0.75);
      g.addColorStop(0,"rgba(0,0,0,0)"); g.addColorStop(1,`rgba(0,0,0,${adjust.vignette/120})`);
      ctx.filter="none"; ctx.fillStyle=g; ctx.fillRect(0,0,canvas.width,canvas.height);
    }
    canvas.toBlob((blob)=>startUpload(new File([blob],file.name,{type:"image/jpeg"})),"image/jpeg",0.92);
  };

  const startUpload=async(uploadFile)=>{
    setUploading(true); setProgress(0);
    const fd=new FormData(); fd.append("userId",userId); fd.append("media",uploadFile);
    const iv=setInterval(()=>setProgress(p=>p>=88?(clearInterval(iv),88):p+8),250);
    try{
      const res=await fetch(`${BASE_URL}/api/status/upload`,{method:"POST",body:fd});
      const data=await res.json();
      clearInterval(iv); setProgress(100);
      if(data.success){
        setFile(null);setPreview(null);setOriginalBlob(null);setSelectedFilter(0);
        setAdjust({brightness:100,contrast:100,saturation:100,warmth:0,fade:0,vignette:0});
        if(fileRef.current)fileRef.current.value="";
        getMyStatus();
      } else alert(data.message);
    } catch{clearInterval(iv);alert("Upload failed ❌");}
    finally{setUploading(false);setTimeout(()=>setProgress(0),900);}
  };

  const deleteStatus=async(id)=>{
    if(!window.confirm("Delete this status?"))return;
    try{const res=await fetch(`${BASE_URL}/api/status/${id}`,{method:"DELETE"});const d=await res.json();if(d.success)getMyStatus();}
    catch(e){console.error(e);}
  };

  const openViewer=async(item)=>{
    setViewer(item);setViewerFilter(0);
    if(userId&&item.user?._id!==userId){
      try{await fetch(`${BASE_URL}/api/status/view/${item._id}`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({userId})});}
      catch(e){console.error(e);}
    }
  };

  const getRemaining=(createdAt)=>{
    const d=new Date(createdAt).getTime()+86400000-Date.now();
    if(d<=0)return"Expired";
    return`${Math.floor(d/3600000)}h ${Math.floor((d%3600000)/60000)}m`;
  };

  const totalPages   = Math.ceil(myStatus.length/statusPerPage);
  const currentItems = myStatus.slice((currentPage-1)*statusPerPage,currentPage*statusPerPage);

  const sliders=[
    {key:"brightness",label:"Brightness ☀",min:50,max:150,step:1},
    {key:"contrast",  label:"Contrast ◑",  min:50,max:180,step:1},
    {key:"saturation",label:"Saturation 🎨",min:0, max:220,step:1},
    {key:"warmth",    label:"Warmth 🔥",    min:0, max:100,step:1},
    {key:"fade",      label:"Fade 🌫",      min:0, max:100,step:1},
    {key:"vignette",  label:"Vignette 🔮",  min:0, max:100,step:1},
  ];

  return (
    <div style={S.root}>
      <style>{CSS}</style>
      <Navbar />
      <div style={S.grain} className="us-grain"/>
      <div style={{...S.orb,...S.orb1}} className="us-orb"/><div style={{...S.orb,...S.orb2}} className="us-orb"/><div style={{...S.orb,...S.orb3}} className="us-orb"/>

      {showCrop&&cropSrc&&<CropEditor src={cropSrc} onDone={onCropDone} onCancel={()=>setShowCrop(false)}/>}

      <div style={S.page}>
        {/* HERO */}
        <div style={S.hero} className="us-fadein">
          <div style={S.heroTag}>STUDIO ✦ STORY</div>
          <h1 style={S.heroTitle}>Your<br/><span style={S.heroAccent} className="us-shimmer">Story</span></h1>
          <p style={S.heroSub}>Upload · Crop · Edit · Share · Expires 24h</p>
        </div>

        {/* STUDIO */}
        <div style={S.studio} className="us-studio">
          {/* Preview */}
          <div style={S.previewZone} onClick={()=>!file&&fileRef.current?.click()}>
            {preview ? (
              file?.type.startsWith("video") ? (
                <video src={preview} style={S.previewMedia} controls/>
              ) : (
                <img src={preview} style={{...S.previewMedia,filter:currentFilter,boxShadow:vigShadow}} alt="preview"/>
              )
            ) : (
              <div style={S.previewEmpty}>
                <div style={{fontSize:52}}>📸</div>
                <p style={{fontSize:15,color:"rgba(240,236,228,0.6)",fontWeight:500}}>Tap to select photo or video</p>
                <span style={{fontFamily:"JetBrains Mono,monospace",fontSize:10,color:"rgba(240,236,228,0.25)",letterSpacing:1}}>Max 30s video · Any image</span>
                <div className="us-border-run" style={{position:"absolute",inset:0,borderRadius:18}}/>
              </div>
            )}
          </div>

          <input ref={fileRef} type="file" accept="image/*,video/*" style={{display:"none"}} onChange={handleFileChange}/>

          {file&&(
            <div style={S.fileInfo}>
              <span>📎 {file.name}</span>
              <div style={{display:"flex",gap:8}}>
                {isImage&&<button style={S.cropBtn} onClick={openCrop}>✂ Crop</button>}
                <button style={S.changeBtn} onClick={()=>fileRef.current?.click()}>Change</button>
              </div>
            </div>
          )}

          {/* EDIT PANEL */}
          {preview&&isImage&&(
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              <div style={S.tabRow}>
                <button style={{...S.tab,...(activeTab==="filter"?S.tabActive:{})}} onClick={()=>setActiveTab("filter")}>🎨 Filters</button>
                <button style={{...S.tab,...(activeTab==="adjust"?S.tabActive:{})}} onClick={()=>setActiveTab("adjust")}>🎛 Adjust</button>
              </div>

              {activeTab==="filter"&&(
                <div style={S.filterStrip} className="us-scroll">
                  {FILTERS.map((f,i)=>(
                    <button key={f.name} style={{...S.filterBtn,...(selectedFilter===i?S.filterBtnActive:{})}} onClick={()=>setSelectedFilter(i)}>
                      <div style={{...S.filterThumb,backgroundImage:`url(${preview})`,filter:f.css}}/>
                      <span style={{fontSize:12}}>{f.emoji}</span>
                      <span style={{fontFamily:"JetBrains Mono,monospace",fontSize:9,color:"rgba(240,236,228,0.5)",letterSpacing:0.5}}>{f.name}</span>
                    </button>
                  ))}
                </div>
              )}

              {activeTab==="adjust"&&(
                <div style={{display:"flex",flexDirection:"column",gap:14}}>
                  {sliders.map(({key,label,min,max,step})=>(
                    <div key={key} style={{display:"flex",flexDirection:"column",gap:6}}>
                      <div style={{display:"flex",justifyContent:"space-between",fontFamily:"JetBrains Mono,monospace",fontSize:11,color:"rgba(240,236,228,0.5)"}}>
                        <span>{label}</span>
                        <span style={{color:"#ffa028"}}>{adjust[key]}{key==="brightness"||key==="contrast"||key==="saturation"?"%":""}</span>
                      </div>
                      <input type="range" min={min} max={max} step={step} value={adjust[key]}
                        onChange={e=>setAdjust(p=>({...p,[key]:Number(e.target.value)}))}
                        style={{width:"100%",height:4,cursor:"pointer",accentColor:"#ffa028"}} className="us-slider"/>
                    </div>
                  ))}
                  <button style={S.resetBtn} onClick={()=>setAdjust({brightness:100,contrast:100,saturation:100,warmth:0,fade:0,vignette:0})}>
                    ↺ Reset Adjustments
                  </button>
                </div>
              )}
            </div>
          )}

          {uploading&&(
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <div style={{flex:1,height:4,background:"rgba(255,255,255,0.07)",borderRadius:20,overflow:"hidden"}}>
                <div style={{height:"100%",background:"linear-gradient(90deg,#ffa028,#ff4d8f)",borderRadius:20,width:`${progress}%`,transition:"width 0.3s ease"}} className="us-progress-shimmer"/>
              </div>
              <span style={{fontFamily:"JetBrains Mono,monospace",fontSize:11,color:"#ffa028",minWidth:32}}>{progress}%</span>
            </div>
          )}

          <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
            {["🌍 Public","⚠ No private content","⏳ Expires 24h","🎥 Max 30s video"].map(w=>(
              <span key={w} style={S.warnTag}>{w}</span>
            ))}
          </div>

          <button style={{...S.uploadBtn,opacity:uploading||!file?0.45:1}} onClick={handleUpload} disabled={uploading||!file}>
            {uploading?<><span style={S.spinner} className="us-spin"/> Publishing...</>:<>✦ Publish Story</>}
          </button>
        </div>

        {/* MY STORIES */}
        <div>
          <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:24}}>
            <div style={{flex:1,height:1,background:"linear-gradient(90deg,transparent,rgba(255,160,40,0.3),transparent)"}}/>
            <h2 style={{fontFamily:"Bebas Neue,sans-serif",fontSize:22,letterSpacing:3,color:"#ffa028",whiteSpace:"nowrap"}}>✦ My Stories</h2>
            <div style={{flex:1,height:1,background:"linear-gradient(90deg,transparent,rgba(255,160,40,0.3),transparent)"}}/>
          </div>

          {myStatus.length===0?(
            <div style={{textAlign:"center",padding:"60px 20px",border:"1px dashed rgba(255,255,255,0.06)",borderRadius:20}}>
              <div style={{fontSize:52,marginBottom:16}}>🎞</div>
              <p style={{fontFamily:"Bebas Neue,sans-serif",fontSize:24,letterSpacing:2,color:"rgba(240,236,228,0.35)",marginBottom:6}}>No stories yet</p>
              <span style={{fontFamily:"JetBrains Mono,monospace",fontSize:11,color:"rgba(240,236,228,0.2)",letterSpacing:1}}>Upload your first story above</span>
            </div>
          ):(
            <>
              <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:16}}>
                {currentItems.map((item)=>(
                  <div key={item._id} style={S.card} className="us-card-hover">
                    <div style={{position:"relative",width:"100%",background:"#000",cursor:"pointer",overflow:"hidden"}} onClick={()=>openViewer(item)}>
                      {item.mediaType==="image"
                        ?<img src={item.media} style={{width:"100%",display:"block",objectFit:"contain",maxHeight:280,minHeight:160,transition:"transform 0.3s"}} alt="story"/>
                        :<video src={item.media} style={{width:"100%",display:"block",objectFit:"contain",maxHeight:280,minHeight:160}} muted loop playsInline preload="metadata"
                           onMouseEnter={e=>e.target.play().catch(()=>{})} onMouseLeave={e=>{e.target.pause();e.target.currentTime=0;}}/>
                      }
                      <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center"}} className="us-card-overlay">
                        <span style={{fontSize:28,filter:"drop-shadow(0 2px 8px rgba(0,0,0,0.8))"}} className="us-card-play">{item.mediaType==="video"?"▶":"⤢"}</span>
                      </div>
                    </div>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:6,padding:"8px 10px",borderTop:"1px solid rgba(255,255,255,0.05)"}}>
                      <span style={{fontFamily:"JetBrains Mono,monospace",fontSize:9,color:"rgba(240,236,228,0.3)",flex:1}}>⏳ {getRemaining(item.createdAt)}</span>
                      <button style={S.viewsBtn} onClick={()=>setViewsPopup(item)}>
                        <span>👁</span>
                        <span style={{fontFamily:"JetBrains Mono,monospace",fontSize:10,fontWeight:600,color:"rgba(240,236,228,0.7)"}}>{item.views?.length||0}</span>
                      </button>
                      <button style={{background:"none",border:"none",cursor:"pointer",fontSize:14,opacity:0.35,padding:"2px 4px"}} onClick={()=>deleteStatus(item._id)}>🗑</button>
                    </div>
                  </div>
                ))}
              </div>
              {totalPages>1&&(
                <div style={{display:"flex",justifyContent:"center",gap:10,marginTop:28}}>
                  {Array.from({length:totalPages}).map((_,i)=>(
                    <button key={i} style={{...S.pageDot,...(currentPage===i+1?S.pageDotActive:{})}} onClick={()=>setCurrentPage(i+1)}/>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* VIEWER */}
      {viewer&&(
        <div style={S.viewerOverlay} onClick={()=>setViewer(null)}>
          <div style={S.viewerInner} onClick={e=>e.stopPropagation()}>
            <button style={S.viewerClose} onClick={()=>setViewer(null)}>✕</button>
            <div style={{background:"#000",borderRadius:20,overflow:"hidden",display:"flex",alignItems:"center",justifyContent:"center",minHeight:250}}>
              {viewer.mediaType==="image"
                ?<img src={viewer.media} style={{width:"100%",objectFit:"contain",maxHeight:"65vh",borderRadius:18,display:"block",filter:buildFilter(viewerFilter,{brightness:100,contrast:100,saturation:100,warmth:0,fade:0,vignette:0})}} alt="full"/>
                :<video src={viewer.media} style={{width:"100%",objectFit:"contain",maxHeight:"65vh",borderRadius:18,display:"block"}} controls autoPlay/>
              }
            </div>
            {viewer.mediaType==="image"&&(
              <div style={S.filterStrip} className="us-scroll">
                {FILTERS.map((f,i)=>(
                  <button key={f.name} style={{...S.filterBtn,...(viewerFilter===i?S.filterBtnActive:{})}} onClick={()=>setViewerFilter(i)}>
                    <div style={{...S.filterThumb,backgroundImage:`url(${viewer.media})`,filter:f.css}}/>
                    <span style={{fontSize:9,color:"rgba(240,236,228,0.45)"}}>{f.name}</span>
                  </button>
                ))}
              </div>
            )}
            <div style={{display:"flex",justifyContent:"center",gap:20,fontFamily:"JetBrains Mono,monospace",fontSize:11,color:"rgba(240,236,228,0.3)",letterSpacing:1}}>
              <span>👁 {viewer.views?.length||0} views</span>
              <span>⏳ {getRemaining(viewer.createdAt)} remaining</span>
            </div>
          </div>
        </div>
      )}

      {/* VIEWS POPUP */}
      {viewsPopup&&(
        <div style={S.viewsOverlay} onClick={()=>setViewsPopup(null)}>
          <div style={S.viewsModal} onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",alignItems:"center",gap:10,paddingBottom:14,borderBottom:"1px solid rgba(255,255,255,0.06)"}}>
              <h3 style={{fontFamily:"Bebas Neue,sans-serif",fontSize:20,letterSpacing:2,color:"#f0ece4",flex:1}}>👁 Viewed by</h3>
              <span style={{fontFamily:"JetBrains Mono,monospace",fontSize:11,color:"#ffa028",background:"rgba(255,160,40,0.1)",border:"1px solid rgba(255,160,40,0.25)",borderRadius:20,padding:"3px 10px"}}>{viewsPopup.views?.length||0} people</span>
              <button style={{background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",color:"rgba(240,236,228,0.5)",width:30,height:30,borderRadius:"50%",fontSize:13,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setViewsPopup(null)}>✕</button>
            </div>
            <div style={{overflowY:"auto",display:"flex",flexDirection:"column",gap:10,flex:1}}>
              {!viewsPopup.views||viewsPopup.views.length===0?(
                <div style={{textAlign:"center",padding:"40px 20px"}}>
                  <div style={{fontSize:36}}>👁</div>
                  <p style={{fontFamily:"Bebas Neue,sans-serif",fontSize:20,color:"rgba(240,236,228,0.3)",marginTop:12}}>No views yet</p>
                </div>
              ):viewsPopup.views.map(v=>(
                <div key={v._id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 12px",borderRadius:14,background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.05)"}}>
                  <img src={v.profilePic||"https://cdn-icons-png.flaticon.com/512/149/149071.png"} style={{width:38,height:38,borderRadius:"50%",objectFit:"cover",border:"1.5px solid rgba(255,160,40,0.3)",flexShrink:0}} alt={v.name}/>
                  <span style={{fontFamily:"DM Sans,sans-serif",fontSize:14,fontWeight:500,color:"#f0ece4",flex:1}}>{v.name}</span>
                  <span style={{color:"rgba(255,160,40,0.6)"}}>✓</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// STYLE TOKENS
// ─────────────────────────────────────────────────────────
const S = {
  root:          {minHeight:"100vh",background:"#06080f",overflowX:"hidden",position:"relative",fontFamily:"DM Sans,sans-serif",color:"#f0ece4"},
  grain:         {position:"fixed",inset:0,zIndex:1,pointerEvents:"none",opacity:0.045,backgroundImage:`url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,backgroundSize:"200px 200px"},
  orb:           {position:"fixed",borderRadius:"50%",filter:"blur(90px)",pointerEvents:"none",zIndex:0},
  orb1:          {width:380,height:380,background:"rgba(255,160,40,0.12)",top:-100,left:-80},
  orb2:          {width:300,height:300,background:"rgba(220,60,120,0.1)",bottom:100,right:-80},
  orb3:          {width:200,height:200,background:"rgba(60,200,180,0.08)",top:"50%",left:"60%"},
  page:          {position:"relative",zIndex:2,maxWidth:620,margin:"0 auto",padding:"80px 20px 80px"},
  hero:          {textAlign:"center",marginBottom:40},
  heroTag:       {display:"inline-block",fontFamily:"JetBrains Mono,monospace",fontSize:10,letterSpacing:4,color:"rgba(255,160,40,0.7)",border:"1px solid rgba(255,160,40,0.25)",borderRadius:20,padding:"4px 14px",marginBottom:16,background:"rgba(255,160,40,0.05)"},
  heroTitle:     {fontFamily:"Bebas Neue,sans-serif",fontSize:"clamp(64px,16vw,110px)",lineHeight:0.9,letterSpacing:-2,color:"#f0ece4",textShadow:"0 0 60px rgba(255,160,40,0.15)",marginBottom:14},
  heroAccent:    {background:"linear-gradient(135deg,#ffa028,#ff4d8f,#ff6b35)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",backgroundClip:"text",backgroundSize:"200%",display:"inline-block"},
  heroSub:       {fontFamily:"JetBrains Mono,monospace",fontSize:11,color:"rgba(240,236,228,0.35)",letterSpacing:2},
  studio:        {background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:28,padding:"28px 24px",marginBottom:48,display:"flex",flexDirection:"column",gap:18,backdropFilter:"blur(20px)",boxShadow:"0 30px 80px rgba(0,0,0,0.4),inset 0 1px 0 rgba(255,255,255,0.05)"},
  previewZone:   {position:"relative",width:"100%",minHeight:300,borderRadius:18,overflow:"hidden",background:"rgba(255,255,255,0.03)",border:"1.5px dashed rgba(255,160,40,0.25)",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer"},
  previewMedia:  {width:"100%",maxHeight:400,objectFit:"contain",display:"block",borderRadius:16,background:"#000"},
  previewEmpty:  {display:"flex",flexDirection:"column",alignItems:"center",gap:10,padding:40,textAlign:"center",position:"relative"},
  fileInfo:      {display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:12,padding:"10px 14px",fontFamily:"JetBrains Mono,monospace",fontSize:11,color:"rgba(240,236,228,0.4)"},
  changeBtn:     {background:"rgba(255,160,40,0.12)",border:"1px solid rgba(255,160,40,0.3)",color:"#ffa028",borderRadius:8,padding:"4px 10px",fontFamily:"JetBrains Mono,monospace",fontSize:10,cursor:"pointer",whiteSpace:"nowrap"},
  cropBtn:       {background:"rgba(80,200,150,0.12)",border:"1px solid rgba(80,200,150,0.35)",color:"#50c896",borderRadius:8,padding:"4px 10px",fontFamily:"JetBrains Mono,monospace",fontSize:10,cursor:"pointer",whiteSpace:"nowrap",fontWeight:600},
  tabRow:        {display:"flex",gap:8,background:"rgba(255,255,255,0.04)",borderRadius:14,padding:4},
  tab:           {flex:1,padding:"8px 12px",background:"transparent",border:"none",borderRadius:10,color:"rgba(240,236,228,0.4)",cursor:"pointer",fontFamily:"DM Sans,sans-serif",fontSize:13,fontWeight:500,transition:"all 0.2s"},
  tabActive:     {background:"rgba(255,160,40,0.15)",color:"#ffa028",border:"1px solid rgba(255,160,40,0.3)"},
  filterStrip:   {display:"flex",gap:10,overflowX:"auto",paddingBottom:8},
  filterBtn:     {flexShrink:0,display:"flex",flexDirection:"column",alignItems:"center",gap:4,background:"none",border:"1.5px solid rgba(255,255,255,0.07)",borderRadius:14,padding:"6px 8px",cursor:"pointer",transition:"all 0.2s",width:68},
  filterBtnActive:{borderColor:"#ffa028",background:"rgba(255,160,40,0.1)",boxShadow:"0 0 14px rgba(255,160,40,0.25)"},
  filterThumb:   {width:50,height:50,borderRadius:8,backgroundSize:"cover",backgroundPosition:"center"},
  resetBtn:      {background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.08)",color:"rgba(240,236,228,0.4)",borderRadius:10,padding:"8px 16px",cursor:"pointer",fontFamily:"JetBrains Mono,monospace",fontSize:11},
  warnTag:       {fontFamily:"JetBrains Mono,monospace",fontSize:10,color:"rgba(255,160,40,0.55)",background:"rgba(255,160,40,0.06)",border:"1px solid rgba(255,160,40,0.15)",borderRadius:20,padding:"3px 10px",letterSpacing:0.5},
  uploadBtn:     {display:"flex",alignItems:"center",justifyContent:"center",gap:8,width:"100%",padding:14,border:"none",borderRadius:16,background:"linear-gradient(135deg,#ffa028,#ff4d8f)",color:"#fff",fontFamily:"Bebas Neue,sans-serif",fontSize:20,letterSpacing:2,cursor:"pointer",boxShadow:"0 8px 28px rgba(255,100,50,0.35)",transition:"all 0.3s"},
  spinner:       {width:14,height:14,border:"2px solid rgba(255,255,255,0.3)",borderTopColor:"#fff",borderRadius:"50%",display:"inline-block"},
  card:          {borderRadius:20,overflow:"hidden",background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",transition:"all 0.3s"},
  viewsBtn:      {display:"flex",alignItems:"center",gap:4,background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:20,padding:"3px 8px",cursor:"pointer",flexShrink:0},
  pageDot:       {width:10,height:10,borderRadius:"50%",background:"rgba(255,255,255,0.12)",border:"1px solid rgba(255,255,255,0.1)",cursor:"pointer",transition:"all 0.25s"},
  pageDotActive: {background:"#ffa028",borderColor:"#ffa028",transform:"scale(1.4)",boxShadow:"0 0 10px rgba(255,160,40,0.5)"},
  viewerOverlay: {position:"fixed",inset:0,zIndex:9999,background:"rgba(0,0,0,0.94)",backdropFilter:"blur(12px)",display:"flex",alignItems:"center",justifyContent:"center"},
  viewerInner:   {position:"relative",width:"100%",maxWidth:520,maxHeight:"95vh",display:"flex",flexDirection:"column",gap:14,padding:20,overflowY:"auto"},
  viewerClose:   {position:"absolute",top:0,right:20,background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.12)",color:"#fff",width:36,height:36,borderRadius:"50%",fontSize:16,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",zIndex:10},
  viewsOverlay:  {position:"fixed",inset:0,zIndex:9998,background:"rgba(0,0,0,0.7)",backdropFilter:"blur(8px)",display:"flex",alignItems:"flex-end",justifyContent:"center"},
  viewsModal:    {width:"100%",maxWidth:500,background:"linear-gradient(160deg,#0f0c1a 0%,#1a1020 100%)",border:"1px solid rgba(255,160,40,0.2)",borderRadius:"24px 24px 0 0",padding:"24px 20px",maxHeight:"70vh",display:"flex",flexDirection:"column",gap:16},
  // crop
  cropOverlay:   {position:"fixed",inset:0,zIndex:10000,background:"rgba(0,0,0,0.97)",backdropFilter:"blur(16px)",display:"flex",alignItems:"center",justifyContent:"center"},
  cropModal:     {width:"100%",maxWidth:560,display:"flex",flexDirection:"column",gap:14,padding:"20px 16px",maxHeight:"98vh",overflowY:"auto"},
  cropTopBar:    {display:"flex",alignItems:"center",justifyContent:"space-between"},
  cropBarBtn:    {background:"rgba(255,255,255,0.07)",border:"1px solid rgba(255,255,255,0.1)",color:"rgba(240,236,228,0.6)",borderRadius:10,padding:"6px 14px",cursor:"pointer",fontFamily:"JetBrains Mono,monospace",fontSize:11},
  cropTitle:     {fontFamily:"Bebas Neue,sans-serif",fontSize:22,letterSpacing:3,color:"#f0ece4"},
  ratioPills:    {display:"flex",gap:8,overflowX:"auto",paddingBottom:4},
  ratioPill:     {flexShrink:0,display:"flex",alignItems:"center",gap:5,background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:20,padding:"5px 12px",cursor:"pointer",fontFamily:"JetBrains Mono,monospace",fontSize:11,color:"rgba(240,236,228,0.5)",transition:"all 0.2s"},
  ratioPillActive:{background:"rgba(255,160,40,0.15)",borderColor:"#ffa028",color:"#ffa028"},
  cropCanvasWrap:{display:"flex",justifyContent:"center",background:"#000",borderRadius:16,overflow:"hidden",minHeight:250},
};

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500&family=JetBrains+Mono:wght@400;500;600&display=swap');
.us-fadein{animation:usFadeUp 0.8s ease both}
@keyframes usFadeUp{from{opacity:0;transform:translateY(22px)}to{opacity:1;transform:translateY(0)}}
.us-shimmer{background-size:200%;animation:usShimmer 4s linear infinite}
@keyframes usShimmer{from{background-position:0% center}to{background-position:200% center}}
.us-border-run{
  background:linear-gradient(90deg,#ffa028,#ff4d8f,#06b6d4,#ffa028);
  background-size:300%;
  animation:usBorderRun 3s linear infinite;
  -webkit-mask:linear-gradient(#fff 0 0) content-box,linear-gradient(#fff 0 0);
  -webkit-mask-composite:xor;mask-composite:exclude;
  padding:1.5px;opacity:0.4;pointer-events:none;
}
@keyframes usBorderRun{from{background-position:0% center}to{background-position:300% center}}
.us-progress-shimmer{background-size:200%;animation:usProg 1.5s linear infinite}
@keyframes usProg{from{background-position:0%}to{background-position:200%}}
.us-spin{animation:usSpin 0.7s linear infinite}
@keyframes usSpin{to{transform:rotate(360deg)}}
.us-scroll{scrollbar-width:none}
.us-scroll::-webkit-scrollbar{display:none}
.us-card-hover:hover{border-color:rgba(255,160,40,0.3)!important;transform:translateY(-5px);box-shadow:0 20px 50px rgba(0,0,0,0.4),0 0 0 1px rgba(255,160,40,0.1)}
.us-card-hover:hover .us-card-overlay{background:rgba(0,0,0,0.25)!important}
.us-card-hover:hover .us-card-play{opacity:1!important;transform:scale(1)!important}
.us-card-overlay{background:rgba(0,0,0,0);transition:background 0.3s}
.us-card-play{opacity:0;transform:scale(0.8);transition:all 0.3s}
.us-slider{-webkit-appearance:none;appearance:none;height:4px;background:rgba(255,255,255,0.1);border-radius:20px;outline:none}
.us-slider::-webkit-slider-thumb{-webkit-appearance:none;appearance:none;width:16px;height:16px;border-radius:50%;background:linear-gradient(135deg,#ffa028,#ff4d8f);cursor:pointer;box-shadow:0 0 8px rgba(255,160,40,0.5);transition:transform 0.15s}
.us-slider::-webkit-slider-thumb:hover{transform:scale(1.25)}
.us-slider::-moz-range-thumb{width:16px;height:16px;border-radius:50%;background:linear-gradient(135deg,#ffa028,#ff4d8f);cursor:pointer;border:none}
/* Mobile: drop the GPU-expensive decorative blur layers (3× blur(90px) orbs,
   feTurbulence grain, card backdrop-blur). These are the main cause of scroll
   lag on phones. Desktop (>768px) is completely unaffected — same look. */
@media (max-width:768px){
  .us-grain,.us-orb{display:none!important}
  .us-studio{backdrop-filter:none!important;-webkit-backdrop-filter:none!important}
}
`;

export default UploadStatus;